
import React from "react";
import { useQuery as useConvexQuery, useMutation as useConvexMutation } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";

// Helper function to check if a value is a Convex wrapped undefined
const isConvexUndefined = (value: any): boolean => {
  return value && typeof value === 'object' && value._type === 'undefined';
};

// Helper function to clean Convex wrapped values
const cleanConvexValue = (value: any): any => {
  if (isConvexUndefined(value)) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value.map(cleanConvexValue);
  }
  if (typeof value === 'object' && value !== null) {
    const cleaned: any = {};
    for (const [key, val] of Object.entries(value)) {
      cleaned[key] = cleanConvexValue(val);
    }
    return cleaned;
  }
  return value;
};

// Enhanced category cleaning function to handle IDs and tags properly
const cleanCategory = (category: any) => {
  const cleaned = cleanConvexValue(category);
  
  // Ensure _id is a proper string
  if (!cleaned._id || typeof cleaned._id !== 'string') {
    console.error(`[cleanCategory] Category ${cleaned.name} has invalid _id:`, cleaned._id);
    // Generate a fallback ID if needed
    cleaned._id = `category_${cleaned.name?.toLowerCase().replace(/\s+/g, '_') || 'unknown'}`;
  }
  
  // Ensure tags is always an array
  if (!cleaned.tags || !Array.isArray(cleaned.tags)) {
    console.log(`[cleanCategory] Category ${cleaned.name} has invalid tags:`, cleaned.tags);
    cleaned.tags = [];
  }
  
  // Clean each tag name to ensure they're strings
  cleaned.tags = cleaned.tags.filter((tag: any) => typeof tag === 'string' && tag.trim().length > 0);
  
  console.log(`[cleanCategory] Cleaned category ${cleaned.name} with ID: ${cleaned._id} and tags:`, cleaned.tags);
  return cleaned;
};

export const useLevelAppCategories = (appFilter: any, isEditMode: boolean = false) => {
  const { toast } = useToast();

  // Fetch all categories from Convex
  const allCategories = useConvexQuery(api.appCategories.list.categories, {});

  // Clean and memoize categories with enhanced ID and tag handling
  const categories = React.useMemo(() => {
    if (!allCategories) return [];
    
    console.log('[useLevelAppCategories] Raw categories from Convex:', allCategories);
    const cleanedCategories = allCategories.map(category => cleanCategory(category));
    console.log('[useLevelAppCategories] Cleaned categories with proper IDs:', cleanedCategories);
    
    return cleanedCategories;
  }, [allCategories]);

  // Helper function to determine status from appFilter
  const getCategoryStatus = React.useCallback((categoryName: string) => {
    console.log(`[getCategoryStatus] Checking status for category: ${categoryName}`);
    console.log(`[getCategoryStatus] AppFilter:`, appFilter);
    
    if (!appFilter) {
      console.log(`[getCategoryStatus] No appFilter, returning disabled`);
      return 'disabled';
    }
    
    // Clean the appFilter to handle Convex wrapped values
    const cleanedAppFilter = cleanConvexValue(appFilter);
    const filterRule = cleanedAppFilter[categoryName];
    
    console.log(`[getCategoryStatus] Clean filter rule for ${categoryName}:`, filterRule);
    
    // Handle undefined or null values
    if (filterRule === undefined || filterRule === null) {
      console.log(`[getCategoryStatus] Filter rule is undefined/null, returning disabled`);
      return 'disabled';
    }
    
    // If it's a boolean true, it's fully enabled
    if (filterRule === true) {
      console.log(`[getCategoryStatus] Filter rule is true, returning fully_enabled`);
      return 'fully_enabled';
    }
    
    // If it's a boolean false, it's disabled
    if (filterRule === false) {
      console.log(`[getCategoryStatus] Filter rule is false, returning disabled`);
      return 'disabled';
    }
    
    // If it's an object with rules, it's enabled with rules
    if (typeof filterRule === 'object' && filterRule !== null) {
      console.log(`[getCategoryStatus] Filter rule is object, returning enabled_with_rules`);
      return 'enabled_with_rules';
    }
    
    console.log(`[getCategoryStatus] Fallback to disabled for ${categoryName}`);
    return 'disabled';
  }, [appFilter]);

  // Use the existing Convex mutation for updating app filters
  const updateAppFilterMutation = useConvexMutation(api.levels.apps.update);

  const updateAppFilter = async (newAppFilter: any, levelId: string) => {
    try {
      console.log('[updateAppFilter] Starting update with filter:', newAppFilter);
      console.log('[updateAppFilter] Level ID:', levelId);
      
      // Transform the appFilter to match Convex validator expectations
      const transformedFilter: Record<string, any> = {};
      
      Object.entries(newAppFilter).forEach(([categoryName, ruleData]: [string, any]) => {
        console.log(`[updateAppFilter] Processing category: ${categoryName}`, ruleData);
        
        // Extract the actual status from the rule data
        const status = ruleData?.status || ruleData;
        console.log(`[updateAppFilter] Extracted status for ${categoryName}:`, status);
        
        if (status === 'fully_enabled') {
          transformedFilter[categoryName] = true;
          console.log(`[updateAppFilter] Set ${categoryName} to true (fully enabled)`);
        } else if (status === 'enabled_with_rules') {
          // Build the rule object from the condition data
          const convexRule: any = {};
          
          if (ruleData.selectedTagIds && ruleData.selectedTagIds.length > 0) {
            convexRule.include = {
              tags: ruleData.selectedTagIds,
              type: ruleData.logicOperator === 'all_of' ? 'all' : 'any'
            };
          }
          
          if (ruleData.exceptionTagIds && ruleData.exceptionTagIds.length > 0) {
            convexRule.exclude = {
              tags: ruleData.exceptionTagIds,
              type: ruleData.exceptionLogicOperator === 'all_of' ? 'all' : 'any'
            };
          }
          
          // Only add the rule if it has actual content
          if (Object.keys(convexRule).length > 0) {
            transformedFilter[categoryName] = convexRule;
          } else {
            // If no rules are defined, treat as fully enabled
            transformedFilter[categoryName] = true;
          }
          
          console.log(`[updateAppFilter] Set ${categoryName} to rule:`, transformedFilter[categoryName]);
        }
        // Note: disabled categories are not included in the filter at all
      });
      
      console.log('[updateAppFilter] Final transformed filter:', transformedFilter);
      
      // Convert levelId to proper Convex ID format
      const convexLevelId = levelId as any;
      
      await updateAppFilterMutation({
        level: convexLevelId,
        appFilter: transformedFilter
      });
      
      console.log('[updateAppFilter] App filter updated successfully');
      
      toast({
        title: "Success",
        description: "App category rules updated successfully.",
      });
    } catch (error) {
      console.error('[updateAppFilter] Error saving category rules:', error);
      toast({
        title: "Error",
        description: "Failed to save category rules. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  return {
    categories,
    updateAppFilter,
    getCategoryStatus,
    isLoading: allCategories === undefined
  };
};
